/**
 * Portal hover hit box for Explore Unknown vs `unknown_background.mp4`.
 *
 * We cannot see your video — this file is the single place to align the box with the portal in the art.
 * Layout is relative to the same inner container as the scaled game UI (logical 1920×1080 canvas).
 *
 * How to tune (pick one):
 *
 * 1) From your source video frame (recommended)
 *    - Use resolution that matches how you authored (often 1920×1080).
 *    - In an editor, draw a rectangle around the portal or mark its center + size in pixels.
 *    - centerX% = (centerX / frameWidth) * 100, centerY% = (centerY / frameHeight) * 100
 *    - width%  = (portalWidth / frameWidth) * 100, height% = (portalHeight / frameHeight) * 100
 *    - Put those into anchorLeftPercent, anchorTopPercent, widthPercent, heightPercent below.
 *
 * 2) Send us numbers
 *    - Message: center as % from left / top of the frame, then width % and height % of the frame,
 *      or a pixel rect (left, top, right, bottom) + frame width × height — we can convert.
 *
 * 3) Live in the browser
 *    - Set showLayoutGrid: true (see below) — same style of grid as BattleUI dev mode — hover and read
 *      "Cell (col, row)" / "Canvas px" in the tooltip, then tell us those values for the portal.
 *    - Set debugOutline: true, reload, adjust percentages until the dashed outline sits on the portal,
 *    - then set debugOutline back to false.
 *
 * Note: The video uses CSS object-fit: cover; edges of the file may be cropped on some aspect ratios.
 *       If the portal sits in a cropped region, tune using what you *see on screen* (option 3).
 *
 * Shadowverse scope (design target): on the order of ~500 encounters across ~100 maps once complete.
 * Per-map POIs and global completion live in layout/DB; per-run scoring uses `explore_unknown_runs`.
 */

export const EXPLORE_UNKNOWN_PORTAL_LAYOUT = {
  /** Aligned from grid: canvas rect (1052, 90)–(1534, 608) → center & size vs 1920×1080. */
  /** Horizontal center of the portal hit area (% of container width, 0–100). */
  anchorLeftPercent: (1293 / 1920) * 100,
  /** Vertical center of the portal hit area (% of container height, 0–100). */
  anchorTopPercent: (349 / 1080) * 100,
  /** Width of the hit area (% of container width). */
  widthPercent: (482 / 1920) * 100,
  /** Height of the hit area (% of container height). */
  heightPercent: (518 / 1080) * 100,
  /** While true, draws a faint outline so you can align the box to the video. */
  debugOutline: false,

  /**
   * BattleUI-style grid (set true temporarily to re-align).
   */
  showLayoutGrid: false,

  /**
   * Grid on top of shadowverse_arrival.jpg to mark waypoint positions (set false when done).
   */
  showArrivalLayoutGrid: false,

  /**
   * Grid on shadowverse_cave.jpg for tuning `cavePointsOfInterest` (1920×1080 canvas px).
   * Set false once coordinates are final.
   */
  showCaveLayoutGrid: false,

  /**
   * Grid on shadowverse_desert.jpg (set true while tuning six waypoints).
   */
  showDesertLayoutGrid: false,

  /**
   * Grid on shadowverse_fair.jpg (set true while tuning seven waypoints).
   */
  showFairLayoutGrid: false,

  /** Grid line spacing in logical pixels (matches BattleUI dev default). */
  gridSize: 20,

  /**
   * Points of interest on `shadowverse_arrival.jpg` (logical canvas px), connected in order.
   * Global mission indices 1–5.
   */
  arrivalPointsOfInterest: [
    [277, 572],
    [752, 570],
    [1273, 430],
    [1514, 349],
    [1304, 292],
  ],

  /** First cave map (`shadowverse_cave.jpg`). Global indices 6–10. */
  cavePointsOfInterest: [
    [1835, 672],
    [1509, 596],
    [917, 434],
    [1130, 329],
    [950, 269],
  ],

  /**
   * Desert map (`shadowverse_desert.jpg`). Global indices 11–16 (six waypoints). Tune in dev or grid.
   */
  desertPointsOfInterest: [[974,736],[593,508],[897,466],[1185,460],[1334,388],[1411,263]],

  arrivalMapImageSrc: '/images/shadowverse_arrival.jpg',
  caveMapImageSrc: '/images/shadowverse_cave.jpg',
  desertMapImageSrc: '/images/shadowverse_desert.jpg',

  /**
   * Fairgrounds map (`shadowverse_fair.jpg`) after the desert slice. Global mission indices 17–23 (seven waypoints).
   * Coordinates are 1920×1080 authoring space — tune with `showFairLayoutGrid` + dev waypoint editor.
   */
  fairMapImageSrc: '/images/shadowverse_fair.jpg',
  fairPointsOfInterest: [[968,702],[340,583],[681,379],[987,467],[1318,647],[1665,611],[1605,495]],

  /**
   * Lavender field (`shadowverse_lavender.jpg`) after the fairgrounds slice. Global missions 24–28 (five waypoints).
   */
  lavenderMapImageSrc: '/images/shadowverse_lavender.jpg',
  lavenderPointsOfInterest: [[238,542],[559,465],[818,644],[1141,621],[988,500]],
  /**
   * Treetop map (`treetop.jpg`) after the lavender slice. Single continuation waypoint.
   */
  treetopMapImageSrc: '/images/treetop.jpg',
  treetopPointsOfInterest: [[1254,416]],
  /**
   * Boat map (`boat.jpg`) after the treetop slice. Global missions 30–34 (five waypoints).
   */
  boatMapImageSrc: '/images/boat.jpg',
  boatPointsOfInterest: [[630,439],[755,553],[926,532],[1318,654],[1756,579]],

  /**
   * Waypoints per arrival and cave surfaces. Desert uses `desertMapSliceMissionCount` (6).
   * Global missions: 1–5 arrival, 6–10 cave, 11–16 desert, 17–23 fair, 24–28 lavender.
   */
  mapSliceMissionCount: 5,

  /** Waypoints on `shadowverse_desert.jpg` (missions 11–16). */
  desertMapSliceMissionCount: 6,

  /** Waypoints on `shadowverse_fair.jpg` (missions 17–23). */
  fairMapSliceMissionCount: 7,

  /** Waypoints on `shadowverse_lavender.jpg` (missions 24–28). */
  lavenderMapSliceMissionCount: 5,
  /** Waypoints on `treetop.jpg` (mission 29). */
  treetopMapSliceMissionCount: 1,
  /** Waypoints on `boat.jpg` (missions 30–34). */
  boatMapSliceMissionCount: 5,

  /**
   * Pixel Past map (`shadowverse_pixelpast.jpg`) after the boat slice. Global missions 35–40 (six waypoints).
   * Coordinates are 1920×1080 authoring space — tune with `showPixelPastLayoutGrid` + dev waypoint editor.
   */
  pixelPastMapImageSrc: '/images/shadowverse_pixelpast.jpg',
  pixelPastPointsOfInterest: [[293, 565], [752, 558], [1156, 394], [1260, 569], [1484, 331], [1292, 233]],
  /** Waypoints on `shadowverse_pixelpast.jpg` (missions 35–40). */
  pixelPastMapSliceMissionCount: 6,

  /**
   * Grid on `shadowverse_pixelpast.jpg` (set true while tuning six waypoints).
   */
  showPixelPastLayoutGrid: false,

  /**
   * Kendrex Colony map (`shadowverse_kendrex.jpg`) after Pixel Past. Global missions 41–46 (six waypoints).
   */
  kendrexMapImageSrc: '/images/shadowverse_kendrex.jpg',
  kendrexPointsOfInterest: [[1050,688],[465,597],[686,345],[1401,330],[1436,588],[1761,664]],
  kendrexMapSliceMissionCount: 6,

  /**
   * Grid on `shadowverse_kendrex.jpg` (set true while tuning six waypoints).
   */
  showKendrexLayoutGrid: false,

  /**
   * Grid on `shadowverse_lavender.jpg` (set true while tuning five waypoints).
   */
  showLavenderLayoutGrid: false,

  /**
   * Rite of respite sits beside the mission that unlocks it; position = that waypoint + offset (authoring px).
   * Tune offsets if the marker sits off-canvas on a given map.
   */
  darknessBargainOffsetBySurface: {
    arrival: [88, -52],
    cave: [-82, -48],
    desert: [92, -46],
    fair: [78, -52],
    lavender: [82, -50],
    kendrex: [86, -48],
  },

  /**
   * Global mission indices that represent **boss missions**. Boss waypoints are drawn as a
   * red warning triangle with a black "DANGER" label on a yellow plate above them, replacing
   * the usual circle marker on the surface map.
   *
   * Boss mission nodes:
   *   - 10 (cave surface, indices 6–10)
   *   - 16 (desert surface, indices 11–16)
   *   - 23 (fair surface, indices 17–23)
   *   - 27 (lavender surface, indices 24–28)
   *   - 33 (boat surface, indices 30–34)
   *   - 40 (pixel past surface, indices 35–40)
   *   - 46 (kendrex colony surface, indices 41–46)
   */
  bossMissionIndices: [10, 16, 23, 27, 33, 40, 46],
};

/**
 * Inclusive global mission index bounds for the fairgrounds surface (`fairMapImageSrc`, e.g. `shadowverse_fair.jpg`).
 * Derived from slice counts so authoring stays consistent when waypoint counts change.
 *
 * @returns {{ fairFirst: number, fairLast: number }}
 */
export function getExploreUnknownFairSliceMissionIndexInclusiveBounds() {
  const L = EXPLORE_UNKNOWN_PORTAL_LAYOUT;
  const mapSliceMissionCount = L.mapSliceMissionCount ?? 5;
  const desertMapSliceMissionCount = L.desertMapSliceMissionCount ?? 6;
  const fairMapSliceMissionCount = L.fairMapSliceMissionCount ?? 7;
  const fairFirst = mapSliceMissionCount * 2 + desertMapSliceMissionCount + 1;
  const fairLast = fairFirst + fairMapSliceMissionCount - 1;
  if (!Number.isInteger(fairFirst) || !Number.isInteger(fairLast) || fairLast < fairFirst) {
    throw new Error('Explore Unknown portal layout: invalid fair slice mission bounds');
  }
  return { fairFirst, fairLast };
}

/**
 * @param {unknown} missionIndex - Global Explore Unknown mission index from `gameState.exploreUnknownMissionIndex`.
 * @returns {boolean}
 */
export function isExploreUnknownFairSliceMissionIndex(missionIndex) {
  if (missionIndex == null || typeof missionIndex !== 'number' || !Number.isInteger(missionIndex)) {
    return false;
  }
  const { fairFirst, fairLast } = getExploreUnknownFairSliceMissionIndexInclusiveBounds();
  return missionIndex >= fairFirst && missionIndex <= fairLast;
}
